/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { TStoryContentElementTypeOEmbedResponse } from '@dl/declarations';
import { OEmbed } from '@dl/ui/components/story/embed/OEmbed';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import {
    DecoratorBlockNode,
    SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    ElementFormatType,
    LexicalNode,
    NodeKey,
    Spread
} from 'lexical';
import { type JSX } from 'react';

type OEmbedComponentProps = Readonly<{
    format: ElementFormatType | null;
    loadingComponent?: JSX.Element | string;
    nodeKey: NodeKey;
    url: string;
    html: string;
    subtype: string;
}>;

function $convertOEmbedElement(
    domNode: HTMLDivElement,
): DOMConversionOutput | null {
    const id = domNode.getAttribute('data-lexical-embed-id');
    const embedType = domNode.getAttribute('data-lexical-embed-type');
    const embedUrl = domNode.getAttribute('data-lexical-embed-url');
    const embedHtml = domNode.getAttribute('data-lexical-embed-html');
    const embedSubtype = domNode.getAttribute('data-lexical-embed-subtype');
    if (id && embedType && embedUrl && embedSubtype && embedHtml) {
        const node = $createOEmbedNode(embedUrl, embedHtml, embedSubtype);
        return { node };
    }
    return null;
}

function OEmbedComponent({
    format,
    nodeKey,
    url,
    html,
    subtype
}: OEmbedComponentProps) {

    return (
        <BlockWithAlignableContents
            className={{
                base: 'EditorTheme__embedBlock',
                focus: 'EditorTheme__embedBlockFocus',
            }}
            format={format}
            nodeKey={nodeKey}
        >
            <OEmbed config={{ subtype, raw_oembed: { _id: '1', html, url } }} />
        </BlockWithAlignableContents>
    )
}

export type SerializedOEmbedNode = Spread<
    {
        url: string;
        html: string;
        subtype: string;
    },
    SerializedDecoratorBlockNode
>;

export class OEmbedNode extends DecoratorBlockNode {
    __url: string;
    __html: string;
    __subtype: string;

    constructor(
        url: string,
        html: string,
        subtype: string,
        format?: ElementFormatType,
        key?: NodeKey
    ) {
        super(format, key);
        this.__url = url;
        this.__html = html;
        this.__subtype = subtype;
    }

    static getType(): TStoryContentElementTypeOEmbedResponse['subtype'] {
        return 'oembed_response';
    }

    static clone(node: OEmbedNode): OEmbedNode {
        return new OEmbedNode(node.__url, node.__html, node.__subtype, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializedOEmbedNode): OEmbedNode {
        return $createOEmbedNode(serializedNode.url, serializedNode.html, serializedNode.subtype).updateFromJSON(serializedNode);
    }

    exportJSON(): SerializedOEmbedNode {
        return {
            ...super.exportJSON(),
            url: this.__url,
            html: this.__html,
            subtype: this.__subtype,
        };
    }

    static importDOM(): DOMConversionMap<HTMLDivElement> | null {
        return {
            div: (domNode: HTMLDivElement) => {

                if (!domNode.hasAttribute('data-lexical-embed-id')) {
                    return null;
                }

                return {
                    conversion: $convertOEmbedElement,
                    priority: 2,
                };
            },
        };
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('div');
        element.setAttribute('data-lexical-embed-html', this.__html);
        element.setAttribute('data-lexical-embed-subtype', this.__subtype);
        const text = document.createTextNode(this.getTextContent());
        element.append(text);
        return { element };
    }

    getId(): string {
        return "oembed_response";
    }

    getTextContent(
        _includeInert?: boolean | undefined,
        _includeDirectionless?: false | undefined,
    ): string {
        return `${this.__type}`;
    }

    decorate(): JSX.Element {
        return (
            <OEmbedComponent
                format={this.__format}
                nodeKey={this.getKey()}
                url={this.__url}
                html={this.__html}
                subtype={this.__subtype}
            />
        )
    }
}

export function $createOEmbedNode(
    url: string,
    html: string,
    subtype: string
): OEmbedNode {
    return new OEmbedNode(url, html, subtype);
}

export function $isOEmbedNode(
    node: OEmbedNode | LexicalNode | null | undefined,
): node is OEmbedNode {
    return node instanceof OEmbedNode;
}
